// frontend/src/components/member-dashboard/components/SessionModal.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import { pricingAPI } from "../../../services/api";
import { formatDateTime } from "../../../utils/dateUtils";
import { isPastDate } from "../../../utils/dateUtils";
import MemberCheckIn from "../../MemberCheckIn";
import { getSessionDisplayName, isMassageSessionType } from "../../../utils/sessionTypeUtils";
import { resolveGuestBadgeVariant } from "../../../utils/addonIndicatorUtils";
import "./SessionModal.css";

// SESSION-MODAL-186-SIBLING — defensive invariant warn (ADR-042 D1):
// addGuest and doubleOccupancy are mutually exclusive at the BE policy
// layer. If a booking carries BOTH, log a console warning for member-side
// visibility (R-024-class signal) and let the row render the safe "Yes"
// fallback. Sibling to StaffSessionModal.js (PR #233) and StaffDashboard.js
// (PR #232) so each consumer of the helper fires its own invariant signal.
const warnDoubleOccupancyAndGuest = (session) => {
  // eslint-disable-next-line no-console
  console.warn(
    "[SESSION-MODAL-186-SIBLING] Session has both addGuest and doubleOccupancy — ADR-042 D1 invariant violation",
    { sessionId: session && session._id }
  );
};

// SESSION-MODAL-186-SIBLING — map resolveGuestBadgeVariant() output to
// the "Add Guest:" row label rendered in the member-facing Session Details
// modal.
// - 'guest'     -> "Yes"          (existing $25 guest-pass path)
// - 'guestCorp' -> "Yes (Corp)"   (corp-tier $15 double-occupancy)
// - 'both'      -> "Yes" + warn   (ADR-042 D1 invariant violation)
// - 'none'      -> "No"           (regression-untouched legacy path)
// Sibling fix to PR #233 StaffSessionModal.js — same resolver, member surface.
const renderAddGuestLabel = (session) => {
  const variant = resolveGuestBadgeVariant(session);
  if (variant === "both") {
    warnDoubleOccupancyAndGuest(session);
    return "Yes";
  }
  if (variant === "guestCorp") return "Yes (Corp)";
  if (variant === "guest") return "Yes";
  return "No";
};

/**
 * SessionModal component for displaying and editing session details
 * @param {Object} props - Component props
 * @param {Object} props.session - Session object
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {Function} props.onSaveChanges - Function to call when changes are saved
 * @param {Function} props.onCancelSession - Function to call when the session is cancelled
 * @param {Function} props.onEditChange - Function to call when an edit field is changed
 * @param {Function} props.onCheckInSuccess - Function to call when check-in is successful
 * @param {Object} props.editingData - Data being edited
 * @param {boolean} props.isEditing - Whether the session is being edited
 * @returns {JSX.Element} - Rendered component
 */
const SessionModal = ({
  session,
  onClose,
  onSaveChanges,
  onCancelSession,
  onEditChange,
  onCheckInSuccess,
  editingData,
  isEditing,
}) => {
  const [addOnSummary, setAddOnSummary] = useState(null);
  const [showGuestPassConfirmation, setShowGuestPassConfirmation] =
    useState(false);
  const [showHalotherapyConfirmation, setShowHalotherapyConfirmation] =
    useState(false);
  const [agreeToPayForGuestPass, setAgreeToPayForGuestPass] = useState(false);
  const [agreeToPayForHalotherapy, setAgreeToPayForHalotherapy] =
    useState(false);

  const appointment = new Date(session.appointmentDate);
  const isPast = isPastDate(appointment);

  // Add-on prices for this appointment's day (Wellness Wednesday guest
  // price on a Wednesday), from Stripe through /api/pricing.
  const [pricing, setPricing] = useState(null);
  const appointmentDateIso = moment(session.appointmentDate)
    .tz("America/Phoenix")
    .format("YYYY-MM-DD");
  useEffect(() => {
    if (!isEditing) return undefined;
    let cancelled = false;
    pricingAPI
      .getPricing(appointmentDateIso)
      .then((response) => {
        if (!cancelled) setPricing(response.data);
      })
      .catch(() => {
        if (!cancelled) setPricing(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isEditing, appointmentDateIso]);
  const formatAddOnPrice = (item) => {
    if (!item) return null;
    const dollars = item.amountCents / 100;
    return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  };
  const guestPriceText = formatAddOnPrice(pricing?.addOns?.guest);
  const halotherapyPriceText = formatAddOnPrice(pricing?.addOns?.halotherapy);

  const sessionTypeLabel = getSessionDisplayName(session.sessionType);

  // Fetch add-on summary when entering edit mode
  useEffect(() => {
    const fetchAddOnSummary = async () => {
      if (!isEditing) return;

      try {
        const response = await fetch(
          `${
            process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api"
          }/add-ons/summary`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const addOnData = await response.json();
          setAddOnSummary(addOnData);
        } else {
          console.error("Failed to fetch add-on summary");
        }
      } catch (error) {
        console.error("Error fetching add-on summary:", error);
      }
    };

    fetchAddOnSummary();
  }, [isEditing]);

  // Handle add-on changes with entitlement checking
  const handleAddOnChange = (e) => {
    const { name, checked } = e.target;

    if (name === "addGuest" && checked) {
      // Check if user has available guest passes
      const hasGuestPasses =
        addOnSummary && addOnSummary.data && addOnSummary.data.guestPasses > 0;
      if (!hasGuestPasses && !session.addGuest) {
        // Only show confirmation if this is a new addition (not already had guest)
        setShowGuestPassConfirmation(true);
        return; // Don't update the state yet
      }
    } else if (name === "halotherapy" && checked) {
      // Check if user has available halotherapy sessions
      const hasHalotherapy =
        addOnSummary && addOnSummary.data && addOnSummary.data.halotherapy > 0;
      if (!hasHalotherapy && !session.halotherapy) {
        // Only show confirmation if this is a new addition (not already had halotherapy)
        setShowHalotherapyConfirmation(true);
        return; // Don't update the state yet
      }
    }

    // Reset payment agreements when unchecking
    if (!checked) {
      if (name === "addGuest") {
        setAgreeToPayForGuestPass(false);
        setShowGuestPassConfirmation(false);
      } else if (name === "halotherapy") {
        setAgreeToPayForHalotherapy(false);
        setShowHalotherapyConfirmation(false);
      }
    }

    // Call the original onChange handler
    onEditChange(e);
  };

  // Enhanced save function that includes payment flags
  const handleSaveWithPaymentInfo = (sessionId) => {
    const hasGuestPasses =
      addOnSummary && addOnSummary.data && addOnSummary.data.guestPasses > 0;
    const hasHalotherapy =
      addOnSummary && addOnSummary.data && addOnSummary.data.halotherapy > 0;

    const enhancedEditingData = {
      ...editingData,
      // Add payment and entitlement flags
      useGuestPass:
        editingData.addGuest && hasGuestPasses && !agreeToPayForGuestPass,
      payForGuestPass: editingData.addGuest && agreeToPayForGuestPass,
      useHalotherapyEntitlement:
        editingData.halotherapy && hasHalotherapy && !agreeToPayForHalotherapy,
      payForHalotherapy: editingData.halotherapy && agreeToPayForHalotherapy,
    };

    onSaveChanges(sessionId, enhancedEditingData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Session Details</h2>

        <div className="session-info">
          <p>
            <strong>Type:</strong> {sessionTypeLabel}
          </p>
          <p>
            <strong>Date:</strong> {formatDateTime(appointment)}
          </p>

          {session.suiteAssignment && (
            <p>
              <strong>Suite:</strong> {session.suiteAssignment.number}
              {session.suiteAssignment.handicap ? " (ADA)" : ""}
            </p>
          )}

          {/* Sauna-specific options */}
          {session.sessionType === "infrared" && isEditing ? (
            <div className="session-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="addGuest"
                  checked={editingData.addGuest || false}
                  onChange={handleAddOnChange}
                />
                <span>Add Guest</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="halotherapy"
                  checked={editingData.halotherapy || false}
                  onChange={handleAddOnChange}
                />
                <span>Halotherapy</span>
              </label>
            </div>
          ) : session.sessionType === "infrared" ? (
            <div className="session-options">
              <p>
                <strong>Add Guest:</strong> {renderAddGuestLabel(session)}
              </p>
              <p>
                <strong>Halotherapy:</strong>{" "}
                {session.halotherapy ? "Yes" : "No"}
              </p>
            </div>
          ) : null}

          {session.paymentId && (
            <>
              {isMassageSessionType(session.sessionType) && (
                <p>
                  <strong>Refund policy:</strong> 2+ hours for a full refund, 1-2
                  hours for a 50% refund, and under 1 hour for no refund.
                </p>
              )}
              <p>
                <strong>Payment:</strong> {session.paymentId.status || "Paid"}
                {typeof session.paymentId.amount === "number"
                  ? ` • $${(Number(session.paymentId.amount) / 100).toFixed(2)}`
                  : ""}
                {session.paymentId.refundAmount
                  ? ` • Refunded $${(Number(session.paymentId.refundAmount) / 100).toFixed(2)}`
                  : ""}
              </p>
            </>
          )}
        </div>

        {/* Payment Confirmation Dialogs */}
        {showGuestPassConfirmation && (
          <div className="add-on-confirmation">
            <div className="confirmation-content">
              <h4>Guest Pass Required</h4>
              <p>
                You don't have any guest passes available. Adding a guest will
                cost {guestPriceText || "the guest fee"}
                {pricing?.isWellnessWednesday ? " (Wellness Wednesday price)" : ""}{" "}
                plus 8.6% AZ/Pima sales tax, which will be charged to your card
                on file or collected at the studio.
              </p>
              <div className="confirmation-actions">
                <button
                  type="button"
                  className="confirm-btn"
                  onClick={() => {
                    setAgreeToPayForGuestPass(true);
                    setShowGuestPassConfirmation(false);
                    // Trigger the checkbox change
                    onEditChange({
                      target: { name: "addGuest", checked: true },
                    });
                  }}
                >
                  {guestPriceText
                    ? `Agree to Pay ${guestPriceText}`
                    : "Agree to Pay"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowGuestPassConfirmation(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showHalotherapyConfirmation && (
          <div className="add-on-confirmation">
            <div className="confirmation-content">
              <h4>Halotherapy Add-On Required</h4>
              <p>
                You don't have any halotherapy sessions available. Adding
                halotherapy will cost {halotherapyPriceText || "the halotherapy fee"}{" "}
                plus 8.6% AZ/Pima sales tax, which will be charged to your card
                on file or collected at the studio.
              </p>
              <div className="confirmation-actions">
                <button
                  type="button"
                  className="confirm-btn"
                  onClick={() => {
                    setAgreeToPayForHalotherapy(true);
                    setShowHalotherapyConfirmation(false);
                    // Trigger the checkbox change
                    onEditChange({
                      target: { name: "halotherapy", checked: true },
                    });
                  }}
                >
                  {halotherapyPriceText
                    ? `Agree to Pay ${halotherapyPriceText}`
                    : "Agree to Pay"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowHalotherapyConfirmation(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Check-in Component */}
        {!isPast && !isEditing && session.status !== "completed" && (
          <div className="check-in-section">
            <MemberCheckIn
              bookingId={session._id}
              appointmentDate={session.appointmentDate}
              onCheckInSuccess={onCheckInSuccess}
            />
          </div>
        )}

        <div className="modal-buttons">
          {!isPast && (
            <>
              {session.sessionType === "infrared" && !isEditing && (
                <button onClick={onSaveChanges} className="edit-btn">
                  Edit Session
                </button>
              )}

              {!isEditing && (
                <button
                  onClick={() => onCancelSession(session._id)}
                  className="cancel-btn"
                >
                  Cancel Session
                </button>
              )}

              {isEditing && (
                <button
                  onClick={() => handleSaveWithPaymentInfo(session._id)}
                  className="save-btn"
                >
                  Save Changes
                </button>
              )}
            </>
          )}

          <button onClick={onClose} className="close-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

SessionModal.propTypes = {
  session: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveChanges: PropTypes.func.isRequired,
  onCancelSession: PropTypes.func.isRequired,
  onEditChange: PropTypes.func.isRequired,
  onCheckInSuccess: PropTypes.func.isRequired,
  editingData: PropTypes.object.isRequired,
  isEditing: PropTypes.bool.isRequired,
};

export default SessionModal;
